import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SkillsAssessmentSection = ({ skills, onAdd, onUpdate, onDelete }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newSkill, setNewSkill] = useState({
    name: '',
    category: '',
    proficiency: 3
  });

  const categoryOptions = [
    { value: 'technical', label: 'Technical Skills' },
    { value: 'soft', label: 'Soft Skills' },
    { value: 'language', label: 'Languages' },
    { value: 'tools', label: 'Tools & Software' }
  ];

  const proficiencyLabels = ['Beginner', 'Elementary', 'Intermediate', 'Advanced', 'Expert'];

  const handleAddSkill = () => {
    if (newSkill?.name && newSkill?.category) {
      onAdd(newSkill);
      setNewSkill({ name: '', category: '', proficiency: 3 });
      setIsAdding(false);
    }
  };

  const handleProficiencyChange = (index, value) => {
    onUpdate(index, { proficiency: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">
          Skills Assessment
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsAdding(!isAdding)}
        >
          Add Skill
        </Button>
      </div>
      {isAdding && (
        <div className="bg-muted/50 rounded-lg p-4 md:p-6 border border-border space-y-4">
          <Input
            label="Skill Name"
            type="text"
            placeholder="React, Python, Communication, etc."
            value={newSkill?.name}
            onChange={(e) => setNewSkill({ ...newSkill, name: e?.target?.value })}
            required
          />

          <Select
            label="Category"
            options={categoryOptions}
            value={newSkill?.category}
            onChange={(value) => setNewSkill({ ...newSkill, category: value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Proficiency Level
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="5"
                value={newSkill?.proficiency}
                onChange={(e) => setNewSkill({ ...newSkill, proficiency: parseInt(e?.target?.value) })}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm font-medium text-primary min-w-[100px]">
                {proficiencyLabels?.[newSkill?.proficiency - 1]}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="default" onClick={handleAddSkill}>
              Save Skill
            </Button>
            <Button variant="outline" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {['technical', 'soft', 'language', 'tools']?.map((category) => {
          const categorySkills = skills?.filter((s) => s?.category === category);
          if (categorySkills?.length === 0) return null;

          const categoryLabel = categoryOptions?.find((c) => c?.value === category)?.label;

          return (
            <div key={category} className="bg-card rounded-lg border border-border p-4 md:p-6">
              <h4 className="text-base md:text-lg font-semibold text-card-foreground mb-4 flex items-center gap-2">
                <Icon
                  name={
                    category === 'technical' ? 'Code' :
                    category === 'soft' ? 'Users' :
                    category === 'language' ? 'Globe' : 'Wrench'
                  }
                  size={20}
                  color="var(--color-primary)"
                />
                {categoryLabel}
              </h4>
              <div className="space-y-4">
                {categorySkills?.map((skill, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-card-foreground">
                        {skill?.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {proficiencyLabels?.[skill?.proficiency - 1]}
                        </span>
                        <button
                          onClick={() => onDelete(skills?.indexOf(skill))}
                          className="text-error hover:text-error/80 transition-smooth"
                          aria-label="Delete skill"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${(skill?.proficiency / 5) * 100}%` }}
                        />
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={skill?.proficiency}
                        onChange={(e) => handleProficiencyChange(skills?.indexOf(skill), parseInt(e?.target?.value))}
                        className="w-24 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {skills?.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
          <Icon name="Target" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No skills added yet</p>
          <Button variant="outline" onClick={() => setIsAdding(true)}>
            Add Your First Skill
          </Button>
        </div>
      )}
    </div>
  );
};

export default SkillsAssessmentSection;